import { Col, FlexView, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import Pagination from 'modules/list/pagination';
import Search from 'modules/list/new-search-bar';
import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown, Loader, Popup, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalParentFeedback } from 'actions/eval-prep-visibility';
import { MonoText, PageHeader, RegionPicker } from '../common/filters';
import PastRangePicker, {
  defaultPastRange,
  pastRangeToEpochs,
} from '../common/past-range-picker';
import { COMFORT_LABELS, PACE_LABELS, formatEpochIst } from '../constants';

const PER_PAGE = 20;

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
// gap on Row is vertical padding, not a flex gap, so the spacing is set here.
const ControlRow = styled(Row)`
  flex-wrap: wrap;
  gap: 12px;
  row-gap: 10px;
  padding: 6px 0 10px;
`;
const Stats = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;
const Stat = styled(Col)`
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 96px;
  flex: 0 0 auto;
`;
const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  background: ${({ tone }) => tone.bg};
  color: ${({ tone }) => tone.fg};
`;
const Comment = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.GRAY};
`;

// A parent rating is out of 5; 3 and below is the tail worth reading.
const ratingTone = rating => {
  if (rating >= 4) return { bg: '#e8f3ed', fg: COLORS.DARK_GREEN };
  if (rating === 3) return { bg: '#f9f0e2', fg: COLORS.YELLOW_4 };

  return { bg: '#f8ebeb', fg: COLORS.RUSTY_RED };
};

const ALL_RATINGS = 'all';

const RATING_OPTIONS = [
  { key: ALL_RATINGS, value: ALL_RATINGS, text: 'All ratings' },
  ...[5, 4, 3, 2, 1].map(value => ({
    key: value,
    value,
    text: `${value} ★`,
  })),
];

const EvalParentFeedback = () => {
  const [page, setPage] = useState(1);
  const [rating, setRating] = useState(ALL_RATINGS);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState(null);
  const [range, setRange] = useState(defaultPastRange);

  const {
    data,
    getAll: getFeedback,
    isProcessing,
    error: loadError,
  } = useGetAllEvalParentFeedback();

  const load = useCallback(() => {
    getFeedback({
      page,
      per_page: PER_PAGE,
      ...(rating !== ALL_RATINGS ? { rating } : {}),
      ...(search ? { student_id: search } : {}),
      ...(region ? { region } : {}),
      ...pastRangeToEpochs(range),
    });
  }, [getFeedback, page, rating, search, region, range]);

  useEffect(() => {
    load();
  }, [load]);

  const onRegion = useCallback(next => {
    setRegion(next);
    setPage(1);
  }, []);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const total = data?.pagination?.total ?? null;

  return (
    <Container column>
      <PageHeader title="Parent Class Feedback" />

      <Stats>
        <Stat>
          <Text variant="h6">{summary.total ?? 0}</Text>
          <Text variant="body3" color={COLORS.GRAY}>
            Responses
          </Text>
        </Stat>
        <Popup
          inverted
          size="small"
          position="bottom left"
          content="Mean of every rating in the current filter, out of 5."
          trigger={
            <Stat>
              <Text variant="h6">{summary.average_rating ?? '—'}</Text>
              <Text variant="body3" color={COLORS.GRAY}>
                Average
              </Text>
            </Stat>
          }
        />
        <Popup
          inverted
          size="small"
          position="bottom left"
          content="Ratings of 3 or below — the responses worth reading first."
          trigger={
            <Stat>
              <Text variant="h6">{summary.low_ratings ?? 0}</Text>
              <Text variant="body3" color={COLORS.GRAY}>
                Low ratings
              </Text>
            </Stat>
          }
        />
        <Stat>
          <Text variant="h6">{summary.with_comment ?? 0}</Text>
          <Text variant="body3" color={COLORS.GRAY}>
            With a comment
          </Text>
        </Stat>
      </Stats>

      <Separator transparent height={12} />
      <ControlRow gutter={0} alignItems="center">
        <PastRangePicker value={range} onChange={setRange} />
        <RegionPicker value={region} onChange={onRegion} subject="opportunity" />
        <Dropdown
          compact
          selection
          selectOnBlur={false}
          options={RATING_OPTIONS}
          value={rating}
          onChange={(_, { value }) => {
            setRating(value);
            setPage(1);
          }}
          aria-label="Rating"
        />
        <Search placeholder="Search by student ID" onSearch={setSearch} />
      </ControlRow>

      <TableWrapper>
        {isProcessing ? (
          <Loader active inline="centered">
            Loading feedback...
          </Loader>
        ) : loadError ? (
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            {loadError.message || 'Could not load parent feedback.'}
          </Text>
        ) : (
          <Table
            headerCell={[
              { title: 'Student' },
              { title: 'Coach' },
              { title: 'Rating' },
              { title: 'Comfort' },
              { title: 'Pace' },
              { title: 'What the parent said' },
              { title: 'Shared on' },
            ]}
            dataLength={rows.length}
            havePagination
            scrollBodyHeight={
              isProcessing || !rows.length ? '400px' : 'calc(100vh - 320px)'
            }
            showInfo={false}
            showLoader={isProcessing}
            flexBasis="100%"
            noDataMessage="No parent feedback in this window."
          >
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell width={3} textTransform="none">
                  {row.student_name || '-'}
                  <Separator transparent height={2} />
                  <MonoText>{row.student_id}</MonoText>
                </TableCell>
                <TableCell width={3} textTransform="none">
                  {row.teacher_name || '-'}
                  <Separator transparent height={2} />
                  <MonoText>{row.teacher_id}</MonoText>
                </TableCell>
                <TableCell width={1}>
                  <Chip tone={ratingTone(row.rating)}>{row.rating ?? '—'}</Chip>
                </TableCell>
                <TableCell width={2} textTransform="none">
                  {COMFORT_LABELS[row.comfort] || row.comfort || '—'}
                </TableCell>
                <TableCell width={2} textTransform="none">
                  {PACE_LABELS[row.pace] || row.pace || '—'}
                </TableCell>
                <TableCell width={4} textTransform="none">
                  {row.feedback ? <Comment>{row.feedback}</Comment> : '—'}
                </TableCell>
                <TableCell width={2} textTransform="none">
                  <MonoText>
                    {formatEpochIst(
                      row.feedback_shared_on
                        ? Date.parse(row.feedback_shared_on) / 1000
                        : null,
                    )}
                  </MonoText>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        )}
      </TableWrapper>

      <Separator transparent height={8} />
      <Row gutter={0} justifyContent="center">
        {total > PER_PAGE && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={total}
            onPageChange={setPage}
            shouldShowNewUi
          />
        )}
      </Row>
    </Container>
  );
};

export default EvalParentFeedback;
